import {Component, OnDestroy, OnInit} from '@angular/core';
import {NgIf} from "@angular/common";
import {BankService} from "../../core/services/bank.service";
import {getDefaultPageable, Pageable, PageParams} from "../../core/models/page.model";
import {IBank} from "../../core/models/bank.model";
import {customConfirmationDialog, customDynamicDialog, DEFAULT_ROWS_PER_PAGE_OPTIONS} from "../../core/utils/utils";
import {ToastService} from "../../core/services/toast.service";
import {TableModule} from "primeng/table";
import {DynamicTableComponent} from "../../shared/components/dynamic-table/dynamic-table.component";
import {CustomDialogData} from "../../core/models/custom-dialog.model";
import {BankDetailsComponent} from "./bank-details/bank-details.component";
import {DialogService, DynamicDialogRef} from "primeng/dynamicdialog";
import {PaginatorState} from "primeng/paginator";
import {take} from "rxjs";
import {ToolbarComponent} from "../../shared/components/toolbar/toolbar.component";
import {CustomAction, CustomTableHeader} from "../../core/models/custom-table.model";
import {CardModule} from "primeng/card";
import {SelectModule} from "primeng/select";
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {TooltipModule} from "primeng/tooltip";
import {ConfirmationService} from "primeng/api";
import {SearchFormComponent} from "../../shared/components/search-form/search-form.component";
import {ProgressSpinnerModule} from "primeng/progressspinner";
import {ICurrency} from "../../core/models/devise.model";

@Component({
    selector: 'app-bank',
    imports: [
        // Modules
        TableModule,
        // Components
        DynamicTableComponent, ToolbarComponent,
        // Directives & Pipes
        CardModule, SelectModule, FormsModule, ReactiveFormsModule, TooltipModule, NgIf, SearchFormComponent, ProgressSpinnerModule
    ],
    templateUrl: './bank.component.html',
    styleUrl: './bank.component.scss'
})
export class BankComponent implements OnInit, OnDestroy {

  banks: IBank[] = [];
  columns: CustomTableHeader[] = [
    { key: 'country', column: 'Pays', type: 'country' },
    { key: 'name', column: 'Nom', type: 'text' },
    { key: 'swiftCode', column: 'BIC', type: 'text' },
    { key: 'codeBank', column: 'Code banque', type: 'text' }
  ];
  pageable: PageParams = {page: 0, first: 0, rows: 0, totalRecords: 0, rowPerPageOptions: []};
  ref: DynamicDialogRef | null = null;
  actions: CustomAction[] = [];
  toolbarActions: CustomAction[] = [];

  banksToDisplay: IBank[] = []
  hasSearched = false
  displaySpinner = false

  searchForm!: FormGroup


  constructor(
    private readonly bankService: BankService,
    private readonly toastService: ToastService,
    public dialogService: DialogService,
    private confirmationService: ConfirmationService,
    private formBuilder: FormBuilder
  ) {}

  ngOnInit() {
    this.toolbarActions = [
      {
        role: 'CREATE_BANK',
        icon: 'pi pi-plus',
        label: 'Créer',
        severity: 'primary',
        emit: () => this.createBank(),
        visible: true
      }
    ];
    this.actions = this.getActions();
    // this.loadBanks();
  }

  ngOnDestroy() {
    this.ref?.close();
  }

  getActions(): CustomAction[] {
    return [
      {
        role: 'READ_BANK',
        icon: 'pi pi-info-circle',
        label: 'Consulter',
        severity: 'info',
        emit: (item) => this.viewOrEditBank(item),
        visible: true
      },
      {
        role: 'UPDATE_BANK',
        icon: 'pi pi-pencil',
        label: 'Modifier',
        severity: 'primary',
        emit: (item) => this.viewOrEditBank(item, true),
        visible: true
      },
      {
        role: 'DELETE_BANK',
        icon: 'pi pi-trash',
        label: 'Supprimer',
        severity: 'danger',
        emit: (item: IBank) => this.confirmDeletion(item),
        visible: true
      }
    ];
  }

  loadBanks(doRefresh?: boolean, pageable: Pageable = getDefaultPageable()) {
    let name = this.searchForm?.value.name ?? '';
    let country = this.searchForm?.value.country ?? '';
    console.log("Form values : ", this.searchForm?.value)
    this.bankService.searchBanksByNameAndCountry(name, country, pageable).subscribe({
      next: page => {
        this.banks = page.content;
        if (doRefresh)
          this.banksToDisplay = this.banks
        this.pageable = {
          page: page.number,
          first: page.number * page.size,
          rows: page.size,
          totalRecords: page.totalElements,
          rowPerPageOptions: DEFAULT_ROWS_PER_PAGE_OPTIONS
        };
      },
      error: err => this.toastService.showToast('Récupération des banques', err.error, 'error')
    });
  }

  banksToDisplayChange(event: any) {
    console.log("Banks to display : ", event)
    this.banksToDisplay = event;
    this.pageable = {} as any;
  }

  createBank() {
    const data = {isCreate: true} as CustomDialogData;
    this.ref = this.dialogService.open(BankDetailsComponent, customDynamicDialog('Création banque', data));
    this.ref?.onClose.pipe(take(1)).subscribe(() => this.loadBanks());
  }

  viewOrEditBank(bank: IBank, isEdit: boolean = false) {
    const data = {isView: !isEdit, isEdit, data: bank} as CustomDialogData;
    this.ref = this.dialogService.open(BankDetailsComponent, customDynamicDialog('Détails banque', data));
    this.ref?.onClose.pipe(take(1)).subscribe(() => this.loadBanks());
  }

  confirmDeletion(banque: IBank) {
    const header = 'Suppression d\'une banque';
    const message = `Êtes-vous sûr de vouloir supprimer la banque << <strong> ${banque.name} </strong> >> ?`;
    const options = customConfirmationDialog(header, message, { accept: () => this.deleteBank(banque) })
    this.confirmationService.confirm(options)
  }

  deleteBank(bank: IBank) {
    const summary = 'Suppression banque';
    this.bankService.deleteBank(bank.id).subscribe({
      next: () => {
        this.toastService.showToast(summary, `La banque <strong> << ${bank.name} >> </strong> a été supprimé avec succès.`);
        this.loadBanks(true);
      },
      error: err => {
        this.toastService.showToast(summary, err.error, 'error');
      }
    });
  }

  onPageChange = (event: PaginatorState) => this.loadBanks(true, getDefaultPageable(event.page, event.rows));
}
